import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Users, Share2, Plus, Trash2 } from "lucide-react";

export function SheetUrlField({ url, setUrl }) {
  return (
          <div>
            <Label className="text-[10px] font-condensed tracking-[0.3em] uppercase text-slate-400">
              Google Sheet URL
            </Label>
            <Input
              data-testid="sheet-url-input"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://docs.google.com/spreadsheets/d/…/edit"
              className="mt-2 bg-surface border-surface-3 rounded-none h-11 text-sm"
            />
            <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500 mt-2">
              Sheet must be shared as "Anyone with the link — Viewer" so Gridiron OS can read it.
            </div>
          </div>
  );
}

export function CustomPositionsField({ standard, customs, newCustom, setNewCustom, addCustom, removeCustom }) {
  return (
          <div>
            <Label className="text-[10px] font-condensed tracking-[0.3em] uppercase text-slate-400">
              Custom Positions <span className="text-slate-600">({customs.length}/5)</span>
            </Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {standard.map((p) => (
                <span key={p} className="text-[10px] font-condensed uppercase tracking-widest px-2 py-1 border border-surface-3 text-slate-500">{p}</span>
              ))}
              {customs.map((c) => (
                <span key={c} className="text-[10px] font-condensed uppercase tracking-widest px-2 py-1 border border-amber/50 text-amber flex items-center gap-1" data-testid={`custom-pos-${c}`}>
                  {c}
                  <button onClick={() => removeCustom(c)} className="hover:text-red-400" data-testid={`remove-custom-${c}`}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            {customs.length < 5 && (
              <div className="flex gap-2 mt-3">
                <Input
                  data-testid="new-custom-input"
                  value={newCustom}
                  onChange={(e) => setNewCustom(e.target.value.toUpperCase())}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCustom())}
                  placeholder="e.g. HYBRID"
                  maxLength={8}
                  className="bg-surface border-surface-3 rounded-none h-9 text-sm max-w-[200px]"
                />
                <Button
                  onClick={addCustom}
                  data-testid="add-custom-btn"
                  className="rounded-none bg-surface-2 hover:bg-surface-3 text-white border border-surface-3 hover:border-amber h-9 text-[10px] font-condensed uppercase tracking-widest"
                >
                  <Plus className="w-3 h-3 mr-1" /> Add
                </Button>
              </div>
            )}
          </div>
  );
}

export function PositionCoachesField({ positionsInUse, posCoaches, setPosCoaches, staff }) {
  return (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-4 h-4 text-amber" />
              <Label className="text-[10px] font-condensed tracking-[0.3em] uppercase text-slate-400 mb-0">
                Position Coaches
              </Label>
            </div>
            <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500 mb-3">
              Tag who owns each position group. Shows on the depth chart + printout so gate staff know who to call.
            </div>
            {positionsInUse.length === 0 ? (
              <div className="text-xs text-slate-500 italic">
                Sync a roster first so positions appear here.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {positionsInUse.map((pos) => {
                  const current = posCoaches[pos] || "";
                  return (
                    <div key={pos} className="flex items-center gap-2 border border-surface-3 bg-background px-3 py-2" data-testid={`pos-coach-row-${pos}`}>
                      <span className="text-[10px] font-condensed uppercase tracking-widest text-amber w-12 shrink-0">{pos}</span>
                      <select
                        value={current}
                        onChange={(e) => setPosCoaches({ ...posCoaches, [pos]: e.target.value })}
                        data-testid={`pos-coach-select-${pos}`}
                        className="flex-1 bg-surface border border-surface-3 text-sm h-8 px-2 focus:outline-none focus:border-amber"
                      >
                        <option value="">— Unassigned —</option>
                        {staff.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.name} ({(s.title || s.role || "").replace(/_/g, " ")})
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
  );
}

export function ShareField({ shareable, shared, toggleShare }) {
  return (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Share2 className="w-4 h-4 text-amber" />
              <Label className="text-[10px] font-condensed tracking-[0.3em] uppercase text-slate-400 mb-0">
                Share Roster View With Staff
              </Label>
            </div>
            <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500 mb-3">
              Head coach and AD always have access. Grant view access to additional coaches below.
            </div>
            {shareable.length === 0 ? (
              <div className="text-xs text-slate-500 italic">
                No other coaches on your program yet. Invite coaches from Billing → Coach Seats.
              </div>
            ) : (
              <div className="space-y-2">
                {shareable.map((s) => {
                  const on = shared.includes(s.id);
                  return (
                    <label
                      key={s.id}
                      data-testid={`share-row-${s.id}`}
                      className={`flex items-center gap-3 px-3 py-2 border cursor-pointer transition-colors ${
                        on ? "border-amber bg-amber/5" : "border-surface-3 hover:border-slate-500"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() => toggleShare(s.id)}
                        className="w-4 h-4 accent-amber"
                        data-testid={`share-toggle-${s.id}`}
                      />
                      <div className="flex-1">
                        <div className="text-sm font-semibold">{s.name}</div>
                        <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500">
                          {(s.title || s.role || "").replace(/_/g, " ")} · {s.email}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
  );
}
